// scheduling/components/ScheduleChooserModal.jsx
// Multi-pick staff chooser opened from a backlog card's "Schedule →". Lets the
// user pick WHO to schedule on a job (Me + prior workers + top-5 suggestions),
// allocate the job's remaining budget across the picks, then hand off to the
// grid multi-staff modal (via a `perStaff` pendingAssignment payload — the grid
// shapes each share into a capacity-aware near-term block). Two variants:
//   • mytime — self-focused: only Me pre-selected, others collapsed behind an
//     expander (most My Time users only schedule themselves).
//   • myjobs — team-focused: the full candidate list expanded for multi-pick.
// "View detailed staff suggestions ↗" round-trips to the full StaffSuggestions
// page (?return=chooser) and back here (see StaffDashboard's restore effect).
//
// Capacity: the manager picks a WHEN (start week + # weeks) up front, and every row shows
// that person's contract and free hours in that window, plus a week-by-week strip. The
// window travels to the grid with the handoff, which jumps there and spreads each share
// across it. Opened from the Dashboard (My Jobs, both Backlog tabs) the capacity helpers
// arrive as `capacity`; from the Unscheduled Hours report they are fetched for the window.

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchJobsWIP, fetchJobScheduledStaff, fetchSuggestions } from '../services/schedData';
import { distributeEvenlyQuarters, spreadWithinCapacity } from '../utils/optimizeHours';
import { computeStaffWeeklyUtil } from '../utils/capacity';
import { baseSuggestionFilter, isSchedulableStaff, BRAND, STAFF_DEPT_GROUPS } from '../utils/schedConstants';
import { useGenTrackBands } from '../hooks/useGenTrackBands';
import { useWindowCapacity } from '../hooks/useWindowCapacity';
import { jobBucketOf } from '../utils/genTrack';
import GenTrackFlag from './GenTrackFlag';
import OffOfficeFlag from './OffOfficeFlag';
import OnSiteFilterNote from './OnSiteFilterNote';
import ContractChip from './ContractChip';

// Placeholder levels offered in the "＋ placeholder" picker (mirrors the grid's list).
const PLACEHOLDER_LEVELS = [
  'Intern', 'Consultant', 'Staff A', 'Staff B', 'Senior Accountant',
  'Supervisor', 'Manager', 'Senior Manager', 'Principal', 'Member',
];

const roundH = (n) => Math.round((Number(n) || 0) * 100) / 100;
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// Even-split `total` across the given ids (0.25h-quantized, sums to total).
const splitEven = (total, ids) => {
  const arr = distributeEvenlyQuarters(total, ids.length);
  const out = {};
  ids.forEach((id, i) => { out[id] = arr[i]; });
  return out;
};

// Split `total` across real staff in proportion to their free hours in the window,
// never past anyone's free hours (0.25h-quantized, largest-remainder rounding).
// Placeholders are future people with no capacity of their own: they only take what the
// real staff can't absorb. If nobody has room the remainder simply stays unallocated —
// the meter shows the gap rather than the split pretending it fits.
const splitByRoom = (total, ids, freeOf) => {
  const real = ids.filter(id => typeof id === 'number');
  const phs = ids.filter(id => typeof id !== 'number');
  const totalQ = Math.max(0, Math.round((Number(total) || 0) * 4));
  const freeQ = real.map(id => Math.max(0, Math.floor((freeOf(id) || 0) * 4)));
  const sumFreeQ = freeQ.reduce((s, q) => s + q, 0);
  const out = {};
  let restQ = 0;
  if (sumFreeQ >= totalQ && sumFreeQ > 0) {
    const raw = freeQ.map(q => (totalQ * q) / sumFreeQ);
    const q = raw.map(Math.floor);
    let left = totalQ - q.reduce((s, v) => s + v, 0);
    raw.map((r, i) => [r - q[i], i]).sort((a, b) => b[0] - a[0])
      .forEach(([, i]) => { if (left > 0 && q[i] < freeQ[i]) { q[i] += 1; left -= 1; } });
    real.forEach((id, i) => { out[id] = q[i] / 4; });
  } else {
    real.forEach((id, i) => { out[id] = freeQ[i] / 4; });
    restQ = totalQ - sumFreeQ;
  }
  const phShare = phs.length ? distributeEvenlyQuarters(restQ / 4, phs.length) : [];
  phs.forEach((id, i) => { out[id] = phShare[i]; });
  return out;
};

const isoAddDays = (iso, n) => {
  const d = new Date(iso + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const mondayOf = (iso) => {
  const day = new Date(iso + 'T12:00:00').getDay();
  return isoAddDays(iso, day === 0 ? -6 : 1 - day);
};
const fmtDay = (iso) => new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const WINDOW_WEEKS = [1, 2, 3, 4, 6, 8, 10, 12, 16, 20, 26];

// Default WHEN: the 4 weeks leading into the job's internal due date when it is still
// ahead, else the 4 weeks from this week.
function defaultWindow(job) {
  const thisMonday = mondayOf(todayISO());
  const weeks = 4;
  const due = job?.Deadline ? String(job.Deadline).slice(0, 10) : null;
  if (!due || due < todayISO()) return { start: thisMonday, weeks };
  const start = isoAddDays(mondayOf(due), -7 * (weeks - 1));
  return { start: start > thisMonday ? start : thisMonday, weeks };
}

// One tick per week in the window, coloured by how booked that week already is.
function WeekStrip({ weeks }) {
  if (!weeks?.length) return null;
  return (
    <span className="sched-chooser-strip" aria-hidden="true">
      {weeks.map(w => {
        const cls = w.capacity <= 0 ? 'off' : w.pct > 100 ? 'over' : w.pct >= 80 ? 'busy' : 'open';
        const label = w.capacity <= 0
          ? `Week of ${fmtDay(w.weekStart)}: unavailable`
          : `Week of ${fmtDay(w.weekStart)}: ${w.scheduled}h of ${w.capacity}h booked (${w.pct}%)`;
        return <span key={w.weekStart} className={`sched-chooser-strip-wk sched-chooser-strip-wk--${cls}`} title={label} />;
      })}
    </span>
  );
}

// Contract + free hours in the window + week strip, shared by candidate rows and the
// manual search results. `hours` (when checked) turns the free figure amber/red.
function RoomLine({ room, hours }) {
  if (!room) return null;
  const h = Number(hours) || 0;
  const over = Math.round((h - room.free) * 100) / 100;
  const state = h > 0 && over > 0 ? 'over' : room.free <= 0 ? 'none' : h > 0.8 * room.free ? 'tight' : '';
  return (
    <span className="sched-chooser-room">
      <ContractChip contract={room.contract} />
      <span className={`sched-chooser-free${state ? ` sched-chooser-free--${state}` : ''}`}
        title="Free capacity in the window: contract hours minus PTO, holidays and work already scheduled">
        {room.free}h free{state === 'over' ? ` · ${over}h won't fit` : ''}
      </span>
      <WeekStrip weeks={room.weeks} />
    </span>
  );
}

// One candidate row (checkbox + name/level + score + inline hours). Module-level
// (stable identity) so the hours <input> keeps focus while typing.
function ChooserRow({ id, name, level, meta, score, isMe, checked, hours, badge, room, onToggle, onAlloc }) {
  return (
    <div className={`sched-chooser-row${checked ? ' sched-chooser-row--on' : ''}`} onClick={() => onToggle(id)}>
      <input type="checkbox" checked={checked} readOnly tabIndex={-1} />
      <div className="sched-chooser-row-info">
        <div className="sched-chooser-row-name">{name}{isMe && <span className="sched-chooser-you"> (you)</span>}</div>
        {/* Room rides on the sub line so a row is no taller than before capacity cues. */}
        <div className="sched-chooser-row-sub">
          {[level, meta].filter(Boolean).join(' · ')}
          <RoomLine room={room} hours={checked ? hours : 0} />
        </div>
        {badge}
      </div>
      {score != null && <span className="sched-chooser-score" title="Match score (experience + interest)">{Math.round(score)}</span>}
      {checked && (
        <input type="number" className="sched-chooser-alloc" min="0" step="0.25"
          value={hours ?? ''}
          onClick={e => e.stopPropagation()}
          onChange={e => onAlloc(id, e.target.value)}
          aria-label={`Hours for ${name}`} />
      )}
    </div>
  );
}

export default function ScheduleChooserModal({
  job, meStaffId, meName, staff = [], variant = 'myjobs', initial = null, subTab = 'mytime', origin = 'backlog', onClose,
  capacity = null,
}) {
  const navigate = useNavigate();
  const remaining = roundH(job?.remaining || 0);

  // ── WHEN (start week + # weeks) ──
  const [winStart, setWinStart] = useState(() => initial?.window?.start || defaultWindow(job).start);
  const [winWeeks, setWinWeeks] = useState(() => initial?.window?.weeks || defaultWindow(job).weeks);
  const winEnd = useMemo(() => isoAddDays(winStart, winWeeks * 7 - 1), [winStart, winWeeks]);
  // Bookable days: the window from today on (a past Monday in "this week" isn't free).
  const windowDays = useMemo(() => {
    const today = todayISO();
    const out = [];
    for (let i = 0; i < winWeeks * 7; i++) {
      const iso = isoAddDays(winStart, i);
      if (iso >= today) out.push(iso);
    }
    return out;
  }, [winStart, winWeeks]);
  const weekMondays = useMemo(
    () => Array.from({ length: winWeeks }, (_, i) => isoAddDays(winStart, i * 7)),
    [winStart, winWeeks],
  );

  const cap = useWindowCapacity({ startISO: winStart, endISO: winEnd, provided: capacity });
  // Per-person room in the window, computed once per (window, data) and cached.
  const roomFor = useMemo(() => {
    const cache = new Map();
    return (id) => {
      if (!cap || typeof id !== 'number') return null;
      if (!cache.has(id)) {
        const { freeHours } = spreadWithinCapacity({
          staffId: id, candidateDates: windowDays, totalHours: 0,
          getDayCapFn: cap.getDayCap, dayHoursMap: cap.dayHoursByStaff,
        });
        const util = computeStaffWeeklyUtil({
          staffIds: [id], weekMondays, getDayCap: cap.getDayCap, dayHoursByStaff: cap.dayHoursByStaff,
        });
        cache.set(id, {
          free: roundH(freeHours),
          weeks: util[0]?.weeks || [],
          contract: cap.getContract?.(id, winStart) || null,
        });
      }
      return cache.get(id);
    };
  }, [cap, windowDays, weekMondays, winStart]);
  const freeOf = useCallback((id) => roomFor(id)?.free || 0, [roomFor]);

  // How a structural change (pick/unpick/add) re-splits the budget: whichever split
  // button was used last. Even until capacity has loaded.
  const [splitMode, setSplitMode] = useState('even');
  const resplit = useCallback((ids) => {
    if (!ids.length) return {};
    return splitMode === 'room' && cap ? splitByRoom(remaining, ids, freeOf) : splitEven(remaining, ids);
  }, [splitMode, cap, remaining, freeOf]);

  // serviceDesc is kept ONLY so the Gen Track badge can tell whether this job is on
  // the side a candidate is short of. It is read at line ~132 for baseSuggestionFilter
  // and was previously discarded.
  const [data, setData] = useState({ workers: [], suggestions: [], myWip: 0, mySched: 0, serviceDesc: null, onSite: null, loading: true });
  const [selected, setSelected] = useState(() => new Set(
    initial?.selected?.length ? initial.selected : [meStaffId]
  ));
  const [alloc, setAlloc] = useState(() => {
    if (initial?.allocations) return initial.allocations;
    const ids = initial?.selected?.length ? initial.selected : [meStaffId];
    return splitEven(remaining, ids);
  });
  // mytime starts collapsed (only Me); a restore that already has >1 pick, or a
  // stashed showOthers, opens it. myjobs is always expanded.
  const [showOthers, setShowOthers] = useState(
    variant === 'myjobs' || (initial?.selected?.length || 0) > 1 || !!initial?.showOthers
  );
  // Placeholder picks (dept + level, no real staffId). Keyed by a synthetic 'ph:N' id
  // that lives in `selected`/`alloc` alongside real staffIds; emitted in perStaff as
  // { placeholder:{dept,level}, hours } for the grid to create via upsertPlaceholder.
  const [placeholders, setPlaceholders] = useState(() => (initial?.placeholders || []).map(p => ({ key: p.key, dept: p.dept || '', level: p.level || '' })));
  const phSeqRef = useRef(1 + (initial?.placeholders || []).reduce((m, p) => {
    const n = parseInt(String(p.key).split(':')[1], 10);
    return Number.isFinite(n) ? Math.max(m, n) : m;
  }, -1));
  // Manually-added staff (search picklist) not in workers/suggestions — meta for the "Added" rows.
  const [manualPicks, setManualPicks] = useState(() => initial?.manualPicks || []); // [{ staffId, name, level }]
  const [staffSearch, setStaffSearch] = useState('');

  // Load the job's worker union + suggestions on open.
  useEffect(() => {
    if (!job?.JobId) return undefined;
    let cancelled = false;
    setData(d => ({ ...d, loading: true }));
    (async () => {
      try {
        const [wipRows, schedRows, sug] = await Promise.all([
          fetchJobsWIP({ jobId: job.JobId }),
          fetchJobScheduledStaff({ jobId: job.JobId, today: todayISO() }),
          fetchSuggestions(job.JobId).catch(() => null),
        ]);
        if (cancelled) return;
        // Union WIP + scheduled by staffId (excluding me → me is its own row).
        const map = new Map();
        for (const w of (wipRows || [])) {
          if (w.StaffId === meStaffId || !w.StaffId) continue;
          const e = map.get(w.StaffId) || { staffId: w.StaffId, name: w.StaffName || 'Unknown', level: w.StaffLevel || '', wip: 0, sched: 0 };
          e.wip += w.TotalHours || 0;
          map.set(w.StaffId, e);
        }
        for (const s of (schedRows || [])) {
          if (s.StaffId === meStaffId || !s.StaffId) continue;
          const e = map.get(s.StaffId) || { staffId: s.StaffId, name: s.StaffName || 'Unknown', level: s.Level || '', wip: 0, sched: 0 };
          e.sched += s.TotalHours || 0;
          if (!e.name || e.name === 'Unknown') e.name = s.StaffName || e.name;
          map.set(s.StaffId, e);
        }
        // Drop non-schedulable WIP contributors (admin/ops, "No Selection" automation
        // pseudo-staff like Scan Verify) — they log overhead time but aren't pickable.
        const workers = [...map.values()]
          .filter(w => isSchedulableStaff(w.level))
          .sort((a, b) => (b.wip + b.sched) - (a.wip + a.sched));
        const myWip = (wipRows || []).filter(w => w.StaffId === meStaffId).reduce((t, w) => t + (w.TotalHours || 0), 0);
        const mySched = (schedRows || []).filter(s => s.StaffId === meStaffId).reduce((t, s) => t + (s.TotalHours || 0), 0);
        // Top-5 suggestions, same filter the edit modal uses; drop anyone already listed.
        const cands = sug?.candidates || [];
        const seen = new Set();
        const deduped = cands.filter(c => { if (seen.has(c.StaffId)) return false; seen.add(c.StaffId); return true; });
        const onSite = !!sug?.job?.isOnSite && !!sug?.job?.officeCode;
        const filtered = baseSuggestionFilter(deduped, sug?.job?.serviceDesc, { onSiteOfficeCode: onSite ? sug.job.officeCode : null });
        const already = new Set([meStaffId, ...workers.map(w => w.staffId)]);
        const suggestions = filtered.filter(c => !already.has(c.StaffId)).slice(0, 5);
        setData({
          workers, suggestions, myWip: roundH(myWip), mySched: roundH(mySched),
          serviceDesc: sug?.job?.serviceDesc || null,
          // The suggestions list above is hard office-filtered when the job is on-site.
          // Keeping the office lets the UI SAY so, instead of just showing a short list.
          onSite: onSite ? { officeCode: sug.job.officeCode, office: sug.job.office || null } : null,
          loading: false,
        });
      } catch {
        if (!cancelled) setData({ workers: [], suggestions: [], myWip: 0, mySched: 0, serviceDesc: null, onSite: null, loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, [job?.JobId, meStaffId]);

  const nameFor = useCallback((id) => {
    if (id === meStaffId) return meName || 'You';
    return data.workers.find(w => w.staffId === id)?.name
      || data.suggestions.find(s => s.StaffId === id)?.Name
      || `#${id}`;
  }, [meStaffId, meName, data]);

  // Toggle a candidate; re-even-split the remaining budget across the new set
  // (structural change → rebalance; manual edits are made after the set settles).
  const toggle = useCallback((id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
    setAlloc(resplit([...next]));
  }, [selected, resplit]);

  const splitEvenly = useCallback(() => {
    setSplitMode('even');
    setAlloc(selected.size ? splitEven(remaining, [...selected]) : {});
  }, [selected, remaining]);
  const splitAvailable = useCallback(() => {
    setSplitMode('room');
    setAlloc(selected.size ? splitByRoom(remaining, [...selected], freeOf) : {});
  }, [selected, remaining, freeOf]);

  // ── Placeholder picks (dept + level) ──
  const addPlaceholder = useCallback(() => {
    const key = `ph:${phSeqRef.current++}`;
    setPlaceholders(prev => [...prev, { key, dept: job?.dept || '', level: '' }]);
    const next = new Set(selected); next.add(key);
    setSelected(next);
    setAlloc(resplit([...next]));
  }, [selected, resplit, job]);
  const removePlaceholder = useCallback((key) => {
    setPlaceholders(prev => prev.filter(p => p.key !== key));
    const next = new Set(selected); next.delete(key);
    setSelected(next);
    setAlloc(resplit([...next]));
  }, [selected, resplit]);
  const updatePlaceholder = useCallback((key, field, val) => {
    setPlaceholders(prev => prev.map(p => (p.key === key ? { ...p, [field]: val } : p)));
  }, []);

  // Manual add-staff search (people not in suggestions/WIP). Active + schedulable only.
  const manualMatches = useMemo(() => {
    const q = staffSearch.trim().toLowerCase();
    if (!q) return [];
    return (staff || []).filter(s =>
      s.IsActive && isSchedulableStaff(s.Level) && !selected.has(s.StaffId) &&
      ((s.Name || '').toLowerCase().includes(q) || (s.Level || '').toLowerCase().includes(q))
    ).slice(0, 8);
  }, [staff, staffSearch, selected]);

  // Gen Track balance chip (Trello #309). One cached fetch shared with every other
  // candidate surface. The chip only ever fires when THIS job is the side the
  // candidate is short of, so it is always an argument for picking them.
  const { bandByStaff, lists: genTrackLists } = useGenTrackBands();
  const jobBucket = useMemo(
    () => jobBucketOf({ ServiceDesc: data.serviceDesc }, genTrackLists),
    [data.serviceDesc, genTrackLists],
  );
  // Worker rows come from the WIP/scheduled reads, which carry a level but no
  // department — the chip is Gen-Track-only, so it needs one. The staff prop has it.
  const deptByStaffId = useMemo(() => {
    const m = {};
    (staff || []).forEach(x => { m[x.StaffId] = x.DepartmentCode; });
    return m;
  }, [staff]);
  const flagFor = useCallback(
    (staffId, deptCode) => (
      <GenTrackFlag band={bandByStaff[staffId]} deptCode={deptCode} jobBucket={jobBucket} />
    ),
    [bandByStaff, jobBucket],
  );
  // Worker rows come from the WIP/scheduled reads, which carry neither department nor
  // office. Both chips need one; the staff prop has them.
  const officeByStaffId = useMemo(() => {
    const m2 = {};
    (staff || []).forEach(x => { m2[x.StaffId] = x.OfficeCode; });
    return m2;
  }, [staff]);
  // Off-office chip for the lists that are deliberately NOT office-filtered: prior
  // workers (they already charged time to this job -- that is a fact, not a proposal)
  // and the manual name search (a deliberate pick). Renders null unless the job is
  // on-site and the candidate is elsewhere, so it is safe to hang on every row.
  const offOfficeFor = useCallback(
    (staffId, name) => (
      <OffOfficeFlag staffOffice={officeByStaffId[staffId]} staffName={name}
        isOnSite={!!data.onSite} jobOfficeCode={data.onSite?.officeCode}
        jobOffice={data.onSite?.office} />
    ),
    [officeByStaffId, data.onSite],
  );
  const addManualStaff = useCallback((s) => {
    const next = new Set(selected); next.add(s.StaffId);
    setSelected(next);
    setAlloc(resplit([...next]));
    setManualPicks(prev => prev.some(p => p.staffId === s.StaffId) ? prev : [...prev, { staffId: s.StaffId, name: s.Name, level: s.Level }]);
    setStaffSearch('');
  }, [selected, resplit]);

  const allocatedTotal = useMemo(() =>
    roundH([...selected].reduce((t, id) => t + (Number(alloc[id]) || 0), 0)),
  [selected, alloc]);

  const overAllocated = allocatedTotal > remaining + 0.001;
  const meterPct = remaining > 0 ? Math.min(100, (allocatedTotal / remaining) * 100) : (allocatedTotal > 0 ? 100 : 0);
  const meterColor = overAllocated || allocatedTotal <= 0 ? '#B45309' : BRAND.sageGreen;

  const onSchedule = useCallback(() => {
    const realIds = [...selected].filter(id => typeof id === 'number');
    const perStaff = realIds.map(id => ({ staffId: id, hours: roundH(alloc[id]) }));
    for (const ph of placeholders) {
      if (!ph.dept || !ph.level) continue; // skip incomplete placeholders
      perStaff.push({ placeholder: { dept: ph.dept, level: ph.level }, hours: roundH(alloc[ph.key]) });
    }
    if (!perStaff.length) return;
    try {
      sessionStorage.setItem('pendingAssignment', JSON.stringify({
        jobId: job.JobId, jobName: job.Name, clientName: job.ClientName,
        perStaff, staffIds: realIds, staffNames: realIds.map(nameFor),
        wipTotal: job.wipTotal || 0,
        // The grid moves to this window before seeding, so its capacity maths sees the
        // weeks being planned (it only ever loads the weeks on screen).
        window: { start: winStart, end: winEnd, weeks: winWeeks },
      }));
    } catch { /* ignore */ }
    navigate('/schedule/grid');
  }, [selected, alloc, placeholders, job, nameFor, navigate, winStart, winEnd, winWeeks]);

  const onDetailed = useCallback(() => {
    try {
      sessionStorage.setItem('pendingChooserDraft', JSON.stringify({
        jobId: job.JobId, variant, sub: subTab, origin,
        selected: [...selected], allocations: alloc, remaining,
        priorTeamIds: [...selected], showOthers: true,
        placeholders, manualPicks, dept: job.dept || null,
        jobName: job.Name, clientName: job.ClientName, wipTotal: job.wipTotal || 0,
        Deadline: job.Deadline || null,
        window: { start: winStart, weeks: winWeeks },
      }));
    } catch { /* ignore */ }
    // ws/we point the suggestions heatmap at the same window (it shows up to 13 weeks).
    navigate(`/schedule/suggestions/${job.JobId}?return=chooser&ws=${winStart}&we=${winEnd}`);
  }, [job, variant, subTab, origin, selected, alloc, remaining, placeholders, manualPicks, navigate, winStart, winWeeks, winEnd]);

  const meHours = roundH((data.myWip || 0) + (data.mySched || 0));
  // Selected staff who aren't Me / a WIP worker / a top-5 suggestion — e.g. picked
  // on the detailed suggestions round-trip. Render them so they're visible + editable
  // (names carried back via initial.extraStaff).
  const known = new Set([meStaffId, ...data.workers.map(w => w.staffId), ...data.suggestions.map(s => s.StaffId)]);
  const extras = [...selected]
    .filter(id => typeof id === 'number' && id !== meStaffId && !known.has(id))
    .map(id => {
      const meta = (initial?.extraStaff || []).find(e => e.staffId === id) || manualPicks.find(e => e.staffId === id);
      return { staffId: id, name: meta?.name || `#${id}`, level: meta?.level || '' };
    });
  const workerCount = data.workers.length + data.suggestions.length + extras.length;

  // Stable handler so ChooserRow's number input keeps focus across keystrokes.
  const handleAlloc = useCallback((id, val) => setAlloc(prev => ({ ...prev, [id]: val })), []);

  return (
    <div className="sched-mgr-modal-backdrop" onClick={onClose}>
      <div className="sched-mgr-modal sched-chooser-modal" style={{ width: 560, maxWidth: '94vw' }} onClick={e => e.stopPropagation()}>
        <div className="sched-mgr-modal-header">
          <h3>Schedule staff{job?.ClientName ? ` — ${job.ClientName}` : ''}</h3>
          <button className="sched-mgr-modal-btn sched-mgr-x-btn" onClick={onClose} title="Close">&times;</button>
        </div>

        <div className="sched-chooser-jobline">
          <span className="sched-chooser-jobname">{job?.Name}</span>
          <span className="sched-chooser-remaining">{remaining}h remaining to schedule</span>
        </div>

        {/* WHEN — drives every row's free hours and where the grid lands on hand-off. */}
        <div className="sched-chooser-window">
          <label className="sched-chooser-window-lbl" htmlFor="sched-chooser-win-start">When</label>
          <input id="sched-chooser-win-start" type="date" className="sched-chooser-window-date"
            value={winStart}
            onChange={e => { if (e.target.value) setWinStart(mondayOf(e.target.value)); }}
            title="Start week (snaps to the Monday)" />
          <select className="sched-chooser-window-weeks" value={winWeeks}
            onChange={e => setWinWeeks(parseInt(e.target.value, 10) || 4)} aria-label="Number of weeks">
            {WINDOW_WEEKS.map(w => <option key={w} value={w}>{w} week{w > 1 ? 's' : ''}</option>)}
          </select>
          <span className="sched-chooser-window-range">
            {fmtDay(winStart)} – {fmtDay(winEnd)}
            {!cap && ' · checking capacity…'}
          </span>
        </div>

        {/* Allocation meter */}
        <div className="sched-chooser-meter-wrap">
          <div className="sched-chooser-meter-labels">
            <span style={{ color: meterColor, fontWeight: 700 }}>
              {allocatedTotal}h allocated{overAllocated ? ' — over remaining' : ''}
            </span>
            <span style={{ color: '#6B7280' }}>of {remaining}h · {selected.size} staff</span>
            <button type="button" className={`sched-chooser-spliteven${splitMode === 'even' ? ' sched-chooser-spliteven--on' : ''}`} onClick={splitEvenly}
              disabled={!selected.size} title="Even-split the remaining budget across selected staff">Split evenly</button>
            <button type="button" className={`sched-chooser-spliteven sched-chooser-splitroom${splitMode === 'room' ? ' sched-chooser-spliteven--on' : ''}`} onClick={splitAvailable}
              disabled={!selected.size || !cap}
              title="Split in proportion to each person's free hours in the window, never past what they have free. Placeholders only take what real staff can't absorb.">
              Split by availability
            </button>
          </div>
          <div className="sched-chooser-meter">
            <div className="sched-chooser-meter-fill" style={{ width: `${meterPct}%`, background: meterColor }} />
          </div>
        </div>

        {/* Candidate list */}
        <div className="sched-chooser-list">
          <ChooserRow id={meStaffId} name={meName || 'You'} level="" meta={meHours > 0 ? `${meHours}h on this job` : 'no time logged yet'} isMe
            checked={selected.has(meStaffId)} hours={alloc[meStaffId]} room={roomFor(meStaffId)} onToggle={toggle} onAlloc={handleAlloc} />

          {variant === 'mytime' && !showOthers ? (
            <button type="button" className="sched-chooser-expander" onClick={() => setShowOthers(true)}>
              + Schedule someone else on this job{workerCount > 0 ? ` (${workerCount})` : ''}
            </button>
          ) : (
            <>
              {data.loading && <div className="sched-chooser-empty">Loading staff…</div>}
              {!data.loading && data.workers.length > 0 && (
                <>
                  <div className="sched-chooser-subhdr">Others who worked this job</div>
                  {data.workers.map(w => (
                    <ChooserRow key={w.staffId} id={w.staffId} name={w.name} level={w.level}
                      meta={[w.wip > 0 ? `${roundH(w.wip)}h logged` : null, w.sched > 0 ? `${roundH(w.sched)}h scheduled` : null].filter(Boolean).join(', ')}
                      badge={<>{flagFor(w.staffId, deptByStaffId[w.staffId])}{offOfficeFor(w.staffId, w.name)}</>}
                      checked={selected.has(w.staffId)} hours={alloc[w.staffId]} room={roomFor(w.staffId)} onToggle={toggle} onAlloc={handleAlloc} />
                  ))}
                </>
              )}
              {!data.loading && data.suggestions.length > 0 && (
                <>
                  <div className="sched-chooser-subhdr">
                    ★ Suggested for this job
                    <OnSiteFilterNote isOnSite={!!data.onSite} officeCode={data.onSite?.officeCode}
                      office={data.onSite?.office} style={{ marginLeft: 6 }} />
                  </div>
                  {data.suggestions.map(s => (
                    <ChooserRow key={s.StaffId} id={s.StaffId} name={s.Name} level={s.Level}
                      meta={s.DepartmentCode || ''} score={s.totalScore}
                      badge={flagFor(s.StaffId, s.DepartmentCode)}
                      checked={selected.has(s.StaffId)} hours={alloc[s.StaffId]} room={roomFor(s.StaffId)} onToggle={toggle} onAlloc={handleAlloc} />
                  ))}
                </>
              )}
              {extras.length > 0 && (
                <>
                  <div className="sched-chooser-subhdr">Added</div>
                  {extras.map(e => (
                    <ChooserRow key={e.staffId} id={e.staffId} name={e.name} level={e.level}
                      checked={selected.has(e.staffId)} hours={alloc[e.staffId]} room={roomFor(e.staffId)} onToggle={toggle} onAlloc={handleAlloc} />
                  ))}
                </>
              )}
              {!data.loading && data.workers.length === 0 && data.suggestions.length === 0 && extras.length === 0 && (
                <div className="sched-chooser-empty">No other staff on this job yet.</div>
              )}
            </>
          )}
        </div>

        {/* Pinned under the list rather than at the end of it, so it never needs a scroll. */}
        {(variant === 'myjobs' || showOthers) && (
          <button type="button" className="sched-modal-fullanalysis-link" style={{ margin: '-6px 0 10px' }}
            onClick={onDetailed}
            title="Open the full staff suggestions page (availability + score breakdowns), then return here">
            View detailed staff suggestions ↗
          </button>
        )}

        {/* Manual add-staff search — pinned below the detailed link, above the placeholder
            block. Adds any active + schedulable staffer not in suggestions/WIP. */}
        {(variant === 'myjobs' || showOthers) && (
          <div className="sched-chooser-addsearch">
            <input type="text" className="sched-chooser-search-input"
              placeholder="Add someone else — search staff…"
              value={staffSearch} onChange={e => setStaffSearch(e.target.value)} />
            {staffSearch.trim() && (
              <div className="sched-chooser-search-results">
                {manualMatches.length === 0 ? (
                  <div className="sched-chooser-empty">No matching staff</div>
                ) : manualMatches.map(s => (
                  <div key={s.StaffId} className="sched-chooser-search-opt" onClick={() => addManualStaff(s)}>
                    <span className="sched-chooser-search-nm">{s.Name}</span>
                    <span className="sched-chooser-search-lvl">{s.Level}{s.DepartmentCode ? ` · ${s.DepartmentCode}` : ''}</span>
                    <RoomLine room={roomFor(s.StaffId)} />
                    {flagFor(s.StaffId, s.DepartmentCode)}
                    {offOfficeFor(s.StaffId, s.Name)}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Placeholders — pinned at the very bottom (above the footer). Shown in the
            expanded view (myjobs, or mytime after "Schedule someone else"). */}
        {(variant === 'myjobs' || showOthers) && (
          <div className="sched-chooser-phblock">
            {placeholders.map(ph => (
              <div key={ph.key} className="sched-chooser-row sched-chooser-row--on sched-chooser-ph-row" onClick={e => e.stopPropagation()}>
                <div className="sched-chooser-ph-selects">
                  <select value={ph.dept} onChange={e => updatePlaceholder(ph.key, 'dept', e.target.value)}>
                    <option value="">Dept…</option>
                    {STAFF_DEPT_GROUPS.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
                  </select>
                  <select value={ph.level} onChange={e => updatePlaceholder(ph.key, 'level', e.target.value)}>
                    <option value="">Level…</option>
                    {PLACEHOLDER_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <input type="number" className="sched-chooser-alloc" min="0" step="0.25"
                  value={alloc[ph.key] ?? ''} onChange={e => handleAlloc(ph.key, e.target.value)}
                  aria-label="Placeholder hours" />
                <button type="button" className="sched-chooser-ph-remove" title="Remove placeholder"
                  onClick={() => removePlaceholder(ph.key)}>&times;</button>
              </div>
            ))}
            <button type="button" className="sched-chooser-addph" onClick={addPlaceholder}>
              ＋ Add placeholder (dept + level)
            </button>
          </div>
        )}

        <div className="sched-chooser-footer">
          <button className="sched-mgr-modal-btn sched-chooser-cancel" onClick={onClose}>Cancel</button>
          <button className="sched-assign-btn" onClick={onSchedule} disabled={!selected.size}>
            Schedule {selected.size > 1 ? `${selected.size} staff ` : ''}&rarr;
          </button>
        </div>
      </div>
    </div>
  );
}
